import {
  SupplierFormValues,
  SupplierLocation,
  SupplierContact,
  SupplierCertification,
  SupplierInsurancePolicy,
  SupplierSkuLink,
  emptySupplierLegalEntity,
  emptySupplierCorporateHierarchy,
  emptySupplierCompliance,
} from './types';
import {
  emptyToNull,
  toNumberOrNull,
  toIntOrNull,
  toDateInput,
  numberToString,
  toIntOrZero,
} from '../payloadHelpers';

const splitList = (value: string): string[] =>
  value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');

const joinList = (value: unknown): string =>
  Array.isArray(value) ? value.join(', ') : '';

function mapLocations(locations: SupplierLocation[]) {
  return locations.map((location) => ({
    ...location,
    type: emptyToNull(location.type),
    line1: emptyToNull(location.line1),
    line2: emptyToNull(location.line2),
    city: emptyToNull(location.city),
    county: emptyToNull(location.county),
    isPrimary: location.isPrimary,
    isActive: location.isActive,
    coordinates:
      location.coordinates.lat === 0 && location.coordinates.lon === 0
        ? null
        : location.coordinates,
  }));
}

function mapLegalEntity(legalEntity: SupplierFormValues['legalEntity']) {
  const mapped = {
    legalName: emptyToNull(legalEntity.legalName),
    tradingName: emptyToNull(legalEntity.tradingName),
    companyRegistrationNumber: emptyToNull(legalEntity.companyRegistrationNumber),
    vatNumber: emptyToNull(legalEntity.vatNumber),
    taxId: emptyToNull(legalEntity.taxId),
    jurisdictionCountry: emptyToNull(legalEntity.jurisdictionCountry),
    legalEntityType: emptyToNull(legalEntity.legalEntityType),
    incorporationDate: emptyToNull(legalEntity.incorporationDate),
  };
  return Object.values(mapped).some((value) => value !== null) ? mapped : null;
}

function mapContacts(contacts: SupplierContact[]) {
  const rows = contacts
    .map((contact) => ({
      contactId: emptyToNull(contact.contactId),
      name: emptyToNull(contact.name),
      email: emptyToNull(contact.email),
      phoneCountryCode: emptyToNull(contact.phoneCountryCode),
      phoneNumber: emptyToNull(contact.phoneNumber),
      role: emptyToNull(contact.role),
      locationId: emptyToNull(contact.locationId),
      isActive: contact.isActive,
    }))
    .filter(
      (contact) =>
        contact.name !== null ||
        contact.email !== null ||
        contact.phoneNumber !== null ||
        contact.role !== null
    );
  return rows.length > 0 ? rows : null;
}

function mapCorporateHierarchy(
  hierarchy: SupplierFormValues['corporateHierarchy']
) {
  const mapped = {
    parentSupplierId: emptyToNull(hierarchy.parentSupplierId),
    ultimateParentSupplierId: emptyToNull(hierarchy.ultimateParentSupplierId),
    hierarchyLevel: toIntOrNull(hierarchy.hierarchyLevel),
    regionalGrouping: emptyToNull(hierarchy.regionalGrouping),
  };
  const hasValue =
    mapped.parentSupplierId !== null ||
    mapped.ultimateParentSupplierId !== null ||
    mapped.hierarchyLevel !== null ||
    mapped.regionalGrouping !== null;
  return hasValue ? mapped : null;
}

function mapCertifications(certifications: SupplierCertification[]) {
  const rows = certifications
    .map((certification) => ({
      name: emptyToNull(certification.name),
      issuingBody: emptyToNull(certification.issuingBody),
      certificateNumber: emptyToNull(certification.certificateNumber),
      issueDate: emptyToNull(certification.issueDate),
      expiryDate: emptyToNull(certification.expiryDate),
      status: emptyToNull(certification.status),
      documentUrl: emptyToNull(certification.documentUrl),
    }))
    .filter(
      (certification) =>
        certification.name !== null ||
        certification.issuingBody !== null ||
        certification.certificateNumber !== null
    );
  return rows.length > 0 ? rows : null;
}

function mapInsurancePolicies(policies: SupplierInsurancePolicy[]) {
  const rows = policies
    .map((policy) => ({
      type: emptyToNull(policy.type),
      provider: emptyToNull(policy.provider),
      policyNumber: emptyToNull(policy.policyNumber),
      coverageAmount: toNumberOrNull(policy.coverageAmount),
      currency: emptyToNull(policy.currency),
      expiryDate: emptyToNull(policy.expiryDate),
      documentUrl: emptyToNull(policy.documentUrl),
    }))
    .filter(
      (policy) =>
        policy.type !== null ||
        policy.provider !== null ||
        policy.policyNumber !== null
    );
  return rows.length > 0 ? rows : null;
}

function mapCompliance(compliance: SupplierFormValues['compliance']) {
  const certifications = mapCertifications(compliance.certifications);
  const insurancePolicies = mapInsurancePolicies(compliance.insurancePolicies);
  const esgRating = emptyToNull(compliance.esgRating);
  const esgAssessmentDate = emptyToNull(compliance.esgAssessmentDate);
  const sanctionsCheckStatus = emptyToNull(compliance.sanctionsCheckStatus);
  const sanctionsCheckedAt = emptyToNull(compliance.sanctionsCheckedAt);

  const hasValue =
    certifications !== null ||
    insurancePolicies !== null ||
    esgRating !== null ||
    esgAssessmentDate !== null ||
    sanctionsCheckStatus !== null ||
    sanctionsCheckedAt !== null;

  if (!hasValue) return null;

  return {
    certifications,
    insurancePolicies,
    esgRating,
    esgAssessmentDate,
    sanctionsCheckStatus,
    sanctionsCheckedAt,
  };
}

function mapSuppliedSkus(links: SupplierSkuLink[]) {
  const rows = links
    .filter((link) => link.inventorySkuId && link.inventorySkuId.trim() !== '')
    .map((link) => ({
      inventorySkuId: emptyToNull(link.inventorySkuId),
      skuCode: link.skuCode,
      productName: link.productName,
      supplierSkuCode: link.supplierSkuCode,
      leadTimeMeanDays: toNumberOrNull(link.leadTimeMeanDays),
      leadTimeStdDays: toNumberOrNull(link.leadTimeStdDays),
      minimumOrderQuantity: toNumberOrNull(link.minimumOrderQuantity),
      orderMultiple: toNumberOrNull(link.orderMultiple),
      orderMultipleUnit: link.orderMultipleUnit,
      incoterms: link.incoterms,
      isPreferred: link.isPreferred,
      status: link.status,
    }));
  return rows.length > 0 ? rows : null;
}

export function toSupplierPayload(data: SupplierFormValues) {
  return {
    ...data,
    locations: mapLocations(data.locations),
    legalEntity: mapLegalEntity(data.legalEntity),
    contacts: mapContacts(data.contacts),
    corporateHierarchy: mapCorporateHierarchy(data.corporateHierarchy),
    compliance: mapCompliance(data.compliance),
    suppliedSkus: mapSuppliedSkus(data.suppliedSkus),
    sourceType: data.sourceType,
    brandsSupplied: splitList(data.brandsSupplied),
    coverageBranches: splitList(data.coverageBranches),
    certificationStatus: data.certificationStatus,
    certificationLabel: data.certificationLabel,
    certificationExpiry: emptyToNull(data.certificationExpiry),
    lifecycleStatus: data.lifecycleStatus,
    readinessGaps: toIntOrZero(data.readinessGaps),
  };
}

export function fromApiSupplier(
  supplier: Record<string, any>
): Partial<SupplierFormValues> {
  return {
    legalEntity: supplier.legalEntity
      ? {
          legalName: supplier.legalEntity.legalName ?? '',
          tradingName: supplier.legalEntity.tradingName ?? '',
          companyRegistrationNumber:
            supplier.legalEntity.companyRegistrationNumber ?? '',
          vatNumber: supplier.legalEntity.vatNumber ?? '',
          taxId: supplier.legalEntity.taxId ?? '',
          jurisdictionCountry: supplier.legalEntity.jurisdictionCountry ?? '',
          legalEntityType: supplier.legalEntity.legalEntityType ?? '',
          incorporationDate: toDateInput(
            supplier.legalEntity.incorporationDate
          ),
        }
      : { ...emptySupplierLegalEntity },
    locations: (supplier.locations ?? []).map((location: any) => ({
      locationId: location.locationId ?? '',
      address: location.address ?? '',
      type: location.type ?? '',
      line1: location.line1 ?? '',
      line2: location.line2 ?? '',
      city: location.city ?? '',
      county: location.county ?? '',
      country: location.country ?? '',
      postcode: location.postcode ?? '',
      coordinates: {
        lat: location.coordinates?.lat ?? 0,
        lon: location.coordinates?.lon ?? 0,
      },
      isPrimary: location.isPrimary ?? false,
      isActive: location.isActive ?? true,
    })),
    contacts: (supplier.contacts ?? []).map((contact: any) => ({
      contactId: contact.contactId ?? '',
      name: contact.name ?? '',
      email: contact.email ?? '',
      phoneCountryCode: contact.phoneCountryCode ?? '',
      phoneNumber: contact.phoneNumber ?? '',
      role: contact.role ?? '',
      locationId: contact.locationId ?? '',
      isActive: contact.isActive ?? true,
    })),
    corporateHierarchy: supplier.corporateHierarchy
      ? {
          parentSupplierId: supplier.corporateHierarchy.parentSupplierId ?? '',
          ultimateParentSupplierId:
            supplier.corporateHierarchy.ultimateParentSupplierId ?? '',
          hierarchyLevel: numberToString(
            supplier.corporateHierarchy.hierarchyLevel
          ),
          regionalGrouping: supplier.corporateHierarchy.regionalGrouping ?? '',
        }
      : { ...emptySupplierCorporateHierarchy },
    compliance: supplier.compliance
      ? {
          certifications: (supplier.compliance.certifications ?? []).map(
            (certification: any) => ({
              name: certification.name ?? '',
              issuingBody: certification.issuingBody ?? '',
              certificateNumber: certification.certificateNumber ?? '',
              issueDate: toDateInput(certification.issueDate),
              expiryDate: toDateInput(certification.expiryDate),
              status: certification.status ?? '',
              documentUrl: certification.documentUrl ?? '',
            })
          ),
          insurancePolicies: (supplier.compliance.insurancePolicies ?? []).map(
            (policy: any) => ({
              type: policy.type ?? '',
              provider: policy.provider ?? '',
              policyNumber: policy.policyNumber ?? '',
              coverageAmount: numberToString(policy.coverageAmount),
              currency: policy.currency ?? '',
              expiryDate: toDateInput(policy.expiryDate),
              documentUrl: policy.documentUrl ?? '',
            })
          ),
          esgRating: supplier.compliance.esgRating ?? '',
          esgAssessmentDate: toDateInput(supplier.compliance.esgAssessmentDate),
          sanctionsCheckStatus:
            supplier.compliance.sanctionsCheckStatus ?? '',
          sanctionsCheckedAt: toDateInput(
            supplier.compliance.sanctionsCheckedAt
          ),
        }
      : { ...emptySupplierCompliance },
    suppliedSkus: (supplier.suppliedSkus ?? []).map((link: any) => ({
      inventorySkuId: link.inventorySkuId ?? '',
      skuCode: link.skuCode ?? '',
      productName: link.productName ?? '',
      supplierSkuCode: link.supplierSkuCode ?? '',
      leadTimeMeanDays: numberToString(link.leadTimeMeanDays),
      leadTimeStdDays: numberToString(link.leadTimeStdDays),
      minimumOrderQuantity: numberToString(link.minimumOrderQuantity),
      orderMultiple: numberToString(link.orderMultiple),
      orderMultipleUnit: link.orderMultipleUnit ?? '',
      incoterms: link.incoterms ?? '',
      isPreferred: link.isPreferred ?? false,
      status: link.status ?? '',
    })),
    sourceType: supplier.sourceType ?? '',
    brandsSupplied: joinList(supplier.brandsSupplied),
    coverageBranches: joinList(supplier.coverageBranches),
    certificationStatus: supplier.certificationStatus ?? '',
    certificationLabel: supplier.certificationLabel ?? '',
    certificationExpiry: supplier.certificationExpiry ?? '',
    lifecycleStatus: supplier.lifecycleStatus ?? '',
    readinessGaps: numberToString(supplier.readinessGaps),
  };
}
